import { useSelector } from 'react-redux';
import { URL_LANGUAGES } from 'tradera/lang/constants.mjs';
import { type RootState } from 'tradera/state/configure-store';
import { selectFeatureSwitches } from 'tradera/state/environment/selectors';

export const NEXTWEB_PATHS = [
    '/brands',
    '/how-to-buy',
    '/how-to-sell',
    '/verify/kyc-questionnaire'
];

const PATH_PREFIX = `(/(${URL_LANGUAGES.join('|')}))?`;
const vipPathRegex = new RegExp(`${PATH_PREFIX}/item/[0-9]+/([0-9]+)/`);
const isVipLink = (pathname: string) => vipPathRegex.test(pathname);

const categoryPathRegex = new RegExp(`${PATH_PREFIX}/category/([0-9]+)`);
const isCategoryLink = (pathname: string) => categoryPathRegex.test(pathname);

const searchPathRegex = new RegExp(`${PATH_PREFIX}/search\\?q=.*`);
const isSearchLink = (pathname: string) => searchPathRegex.test(pathname);

const startPagePathRegex = new RegExp(`^${PATH_PREFIX}/?$`);
const isStartPageLink = (pathname: string) => startPagePathRegex.test(pathname);

const isLinkToNextWebPage = (url: URL) =>
    url.searchParams.get('next') === '1' ||
    NEXTWEB_PATHS.some((nextwebPath) => url.pathname.endsWith(nextwebPath));

const isLastDigitEnabled = (digits: string, enabledDigits: Array<string>) =>
    enabledDigits.includes(digits.slice(-1));

const isIn99Percent = (itemId: string) => {
    const lastTwoNumbers = itemId.slice(-2);
    const percent = parseInt(lastTwoNumbers);
    return percent > 0;
};

export const useNextWebSpaLink = (absoluteUrl: URL) => {
    const toggles = useSelector(selectFeatureSwitches) || {};
    const cookies = useSelector<RootState, NodeJS.Dict<string> | null>(
        (state) => state.request?.cookies
    );
    const pathname = absoluteUrl?.pathname;

    if (isStartPageLink(pathname)) {
        if (toggles['next-web-start-page-100-percent']) {
            return true;
        }
        const bucket = cookies?.['experiment_bucket'];
        return (
            toggles['next-web-start-page-10-percent'] &&
            bucket &&
            isLastDigitEnabled(bucket, ['1'])
        );
    }

    if (isVipLink(pathname)) {
        if (toggles['next-web-vip-100-percent']) {
            return true;
        }
        const itemId = pathname.match(vipPathRegex)?.[3] || '';
        if (toggles['next-web-vip-99-percent']) {
            return isIn99Percent(itemId);
        }
        if (toggles['next-web-vip-30-percent']) {
            return isLastDigitEnabled(itemId, ['1', '2', '3']);
        }
        if (toggles['next-web-vip-10-percent']) {
            return isLastDigitEnabled(itemId, ['1']);
        }
    }

    if (isCategoryLink(pathname)) {
        if (toggles['next-web-category-100-percent']) {
            return true;
        }
        const categoryId = pathname.match(categoryPathRegex)?.[3] || '';
        if (toggles['next-web-category-99-percent']) {
            return isIn99Percent(categoryId);
        }
        if (toggles['next-web-category-10-percent']) {
            return isLastDigitEnabled(categoryId, ['1']);
        }
    }

    if (isSearchLink(absoluteUrl.href)) {
        return true;
    }

    return isLinkToNextWebPage(absoluteUrl);
};
